import React, { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { ChevronRight, MessageSquare, Trash2, X } from 'lucide-react';
import { threadPanelShell, threadTitleClass, threadDateClass } from './chatViewTheme';
import { getThreadDisplayTitle } from './threadDisplayTitle';

function groupThreads(threads: any[]) {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekStart = new Date(todayStart);
  weekStart.setDate(weekStart.getDate() - 6);

  const today: any[] = [];
  const thisWeek: any[] = [];
  const older: any[] = [];

  for (const t of threads) {
    const d = new Date(t.updatedAt ?? t.createdAt);
    if (d >= todayStart) today.push(t);
    else if (d >= weekStart) thisWeek.push(t);
    else older.push(t);
  }
  return { today, thisWeek, older };
}

function ThreadItem({ thread, onSelect, onDelete }: any) {
  return (
    <div
      role="button"
      tabIndex={0}
      className="group flex cursor-pointer items-start gap-2 rounded-lg p-2 hover:bg-dark-elevated"
      onClick={() => onSelect(thread.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(thread.id);
        }
      }}
    >
      <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-brand-teal-light" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className={threadTitleClass}>{getThreadDisplayTitle(thread)}</p>
        <p className={threadDateClass}>
          {new Date(thread.updatedAt ?? thread.createdAt).toLocaleDateString()}
        </p>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(thread.id);
        }}
        className="hidden h-6 w-6 shrink-0 items-center justify-center rounded text-slate-500 hover:text-red-400 group-hover:flex"
        aria-label="Delete thread"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function SectionHeader({ label, count, expanded, onToggle }: any) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left"
    >
      <ChevronRight
        className={`h-3 w-3 shrink-0 text-slate-600 transition-transform ${expanded ? 'rotate-90' : ''}`}
        aria-hidden
      />
      <span className="text-[11px] font-medium uppercase tracking-wider text-slate-600">
        {label}
      </span>
      <span className="ml-auto text-[11px] text-slate-700">{count}</span>
    </button>
  );
}

export default function ThreadHistoryPanel({
  open,
  threads,
  loading = false,
  onClose,
  onSelectThread,
  onDeleteThread,
}: any) {
  const reduceMotion = useReducedMotion();
  const [olderExpanded, setOlderExpanded] = useState(false);

  const panelMotion: any = reduceMotion
    ? { initial: false, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { x: '100%', opacity: 0 },
        animate: { x: 0, opacity: 1 },
        exit: { x: '100%', opacity: 0 },
        transition: { type: 'spring' as const, stiffness: 400, damping: 35 },
      };

  const { today, thisWeek, older } = groupThreads(threads);

  return (
    <AnimatePresence>
      {open && (
        <motion.div {...panelMotion} className={threadPanelShell}>
          <div className="flex items-center justify-between border-b border-dark-border px-4 py-3">
            <span className="text-sm font-semibold text-slate-200">Conversation History</span>
            <button
              type="button"
              onClick={onClose}
              className="btn-icon-mdl h-7 w-7"
              aria-label="Close history"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {loading && <p className="p-4 text-center text-xs text-slate-500">Loading…</p>}
            {!loading && threads.length === 0 && (
              <p className="p-4 text-center text-xs text-slate-500">No previous conversations</p>
            )}

            {!loading && today.length > 0 && (
              <div className="mb-1">
                <SectionHeader label="Today" count={today.length} expanded={true} onToggle={() => {}} />
                {today.map((t) => (
                  <ThreadItem key={t.id} thread={t} onSelect={onSelectThread} onDelete={onDeleteThread} />
                ))}
              </div>
            )}

            {!loading && thisWeek.length > 0 && (
              <div className="mb-1">
                <SectionHeader label="This Week" count={thisWeek.length} expanded={true} onToggle={() => {}} />
                {thisWeek.map((t) => (
                  <ThreadItem key={t.id} thread={t} onSelect={onSelectThread} onDelete={onDeleteThread} />
                ))}
              </div>
            )}

            {!loading && older.length > 0 && (
              <div className="mb-1">
                <SectionHeader
                  label="Older"
                  count={older.length}
                  expanded={olderExpanded}
                  onToggle={() => setOlderExpanded((v) => !v)}
                />
                {olderExpanded &&
                  older.map((t) => (
                    <ThreadItem key={t.id} thread={t} onSelect={onSelectThread} onDelete={onDeleteThread} />
                  ))}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
